/**
 * Picks random items for endless / multi-round game loops without
 * repeating recent ones. Maintains a recent-IDs window so the user
 * doesn't get the same item twice in quick succession.
 *
 * Generic over the item type — caller supplies an `idOf` projection
 * so cities (id) and countries (iso_a2) share the same picker.
 *
 * RNG is injectable so daily modes can pass a seeded function from
 * `seededRandom.ts` and get deterministic picks per date. Practice
 * modes use the default `Math.random`.
 *
 * The window size scales with the pool — small pools (e.g. 50 capitals
 * for a continent-only mode) shouldn't try to avoid 30 recents because
 * we'd run out of options. Cap at min(20, pool/3).
 */
export class Picker<T> {
  private readonly pool: readonly T[];
  private readonly idOf: (item: T) => string;
  private readonly rng: () => number;
  private readonly recents: string[] = [];
  private readonly recentLimit: number;

  constructor(pool: readonly T[], idOf: (item: T) => string, rng: () => number = Math.random) {
    this.pool = pool;
    this.idOf = idOf;
    this.rng = rng;
    this.recentLimit = Math.min(20, Math.max(1, Math.floor(pool.length / 3)));
  }

  size(): number {
    return this.pool.length;
  }

  /**
   * Pick an item not in the recent window. If every available item is
   * recent (tiny pools), reuse the oldest. Returns null only when the
   * pool itself is empty.
   */
  next(): T | null {
    if (this.pool.length === 0) return null;
    if (this.pool.length === 1) return this.pool[0];
    const recentSet = new Set(this.recents);
    const eligible = this.pool.filter((item) => !recentSet.has(this.idOf(item)));
    const choices = eligible.length > 0 ? eligible : this.pool;
    const pick = choices[Math.floor(this.rng() * choices.length)];
    this.recents.push(this.idOf(pick));
    if (this.recents.length > this.recentLimit) this.recents.shift();
    return pick;
  }

  /** Clear the recent-history window — useful when changing the filter mid-session. */
  reset(): void {
    this.recents.length = 0;
  }
}
